import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { bindActionCreators } from 'redux';
import {
  Card,
  CardBody,
  CardTitle,
} from 'reactstrap';
import { Link } from 'react-router-dom';
import { Loading } from '../../../../../loadbar/Loading';
import {
  clearVolcanoEventInfo as clearVolcanoEventInfoAc,
  getVolcanoEventReferences as getVolcanoEventReferencesAc,
  getVolcanoEventInfo as getVolcanoEventInfoAc,
  sortReferences as sortReferencesAc,
  toggleColumns as toggleColumnsAc,
} from '../../../../../../redux/volcanoes/event/info/volcanoEventInfoActions';
import { parseAndShortenFloat } from '../../../../../../helperFunctions/helperFunctions';
import { SingleResultGrid } from '../../../../../grid/SingleResultGrid';
import { Column } from '../../../../../grid/Column';
import { ReferencesTable } from '../../../../../table/ReferencesTable';
import { CommentsSection } from '../../../../../form/CommentsSection';
import { isDataManager } from '../../../../../../role/roleUtils';
import { EditIcon } from '../../../../../icon/EditIcon';

const renderEditLink = id => (
  <Link aria-label={`Edit Volcano Event ${id}`} to={`/view/hazards/volcano/event-update/${id}`} target="_blank" rel="noopener noreferrer">
    <EditIcon />
  </Link>
);

const pad = (num) => {
  let str = `${num}`;
  if (str.length === 1) {
    str = `0${str}`;
  }
  return str;
};

// eslint-disable-next-line react/prop-types
const parseDate = ({ row }) => {
  const year = row.get('year');
  const month = row.get('month');
  const day = row.get('day');
  let parsedDate = '';
  if (year) {
    parsedDate = year;
    if (month) {
      parsedDate = `${parsedDate}-${pad(month)}`;
      if (day) {
        parsedDate = `${parsedDate}-${pad(day)}`;
      }
    }
  }
  return (<div>{parsedDate}</div>);
};

class VolcanoEventInfoPageUnwrapped extends Component {
  constructor(props) {
    super(props);
    this.handleToggleColumns = this.handleToggleColumns.bind(this);
  }

  componentDidMount() {
    document.title = 'NCEI Hazard Volcano Event Information';
    const {
      match: { params }, getVolcanoEventReferences, getVolcanoEventInfo,
    } = this.props;
    getVolcanoEventReferences({ id: params.volcanoId });
    getVolcanoEventInfo({ id: params.volcanoId });
  }

  componentWillUnmount() {
    const { clearVolcanoEventInfo } = this.props;
    clearVolcanoEventInfo();
  }

  handleToggleColumns({ visibleColumns, sort }) {
    const {
      visibleColumns: preVisibleColumns, sort: prevSort, sortReferences, toggleColumns,
    } = this.props;
    let changed = false;
    if (!visibleColumns.sort().equals(preVisibleColumns.sort())) {
      toggleColumns(visibleColumns);
      changed = true;
    }
    if (!sort.equals(prevSort)) {
      sortReferences(sort);
      changed = true;
    }
    return changed;
  }

  render() {
    const {
      fetched, volcanoEvent, references, sort, visibleColumns, match: { params: { volcanoId } }, authorities,
    } = this.props;

    const dataManager = isDataManager(authorities);
    if (!fetched) {
      return (<Loading />);
    }


    return (
      <div className="haz-results-container haz-page-container">
        <Card className="haz-page-card">
          <CardBody>
            <CardTitle tag="h1" className="haz-page-card-title">Significant Volcanic Eruption</CardTitle>
            {dataManager && renderEditLink(volcanoId)}

            <div className="haz-info-content">

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Volcano Event Information</CardTitle>
                    <SingleResultGrid data={volcanoEvent}>
                      <Column
                        id="maybeEventDate"
                        width={100}
                        cell={parseDate}
                        helpModalId="volEventDate"
                      >
                        Event Date
                      </Column>
                      <Column id="startDate" width={100} helpModalId="volEventStartDate">Start Date</Column>
                      <Column id="endDate" width={100} helpModalId="volEventEndDate">End Date</Column>
                      <Column id="vei" width={100} helpModalId="volEventVei">VEI</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Volcano Information</CardTitle>
                    <SingleResultGrid data={volcanoEvent}>
                      <Column id="newNum" width={100} helpModalId="volLocNewNum">Volcano Number</Column>
                      <Column id="name" width={200} helpModalId="volLocName">Name</Column>
                      <Column id="location" width={200} helpModalId="volLocRegion">Location</Column>
                      <Column id="latitude" width={100} helpModalId="latitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Latitude</Column>
                      <Column id="longitude" width={100} helpModalId="longitude" cell={({ value }) => (<div>{parseAndShortenFloat(value)}</div>)}>Longitude</Column>
                      <Column id="elevation" width={100} helpModalId="volLocElevation">Elevation (m)</Column>
                      <Column id="morphology" width={85} helpModalId="volLocType">Type</Column>
                      <Column id="status" width={85} helpModalId="volLocStatus">Status</Column>
                      <Column id="timeErupt" width={100} helpModalId="volLocTimeErupt">Time Erupt</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>


              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Volcano Effects</CardTitle>
                    <SingleResultGrid data={volcanoEvent}>
                      <Column id="deaths" width={100} helpModalId="volEventDeaths">Deaths</Column>
                      <Column id="deathsAmountOrder" width={125} helpModalId="volEventDeathsDescription">Death Description</Column>
                      <Column id="missing" width={100} helpModalId="volEventMissing">Missing</Column>
                      <Column id="missingAmountOrder" width={125} helpModalId="volEventMissingDescription">Missing Description</Column>
                      <Column id="injuries" width={100} helpModalId="volEventInjuries">Injuries</Column>
                      <Column id="injuriesAmountOrder" width={125} helpModalId="volEventInjuriesDescription">Injuries Description</Column>
                      <Column id="damageMillionsDollars" width={100} helpModalId="volEventDamage">Damage ($Mil)</Column>
                      <Column id="damageAmountOrder" width={125} helpModalId="volEventDamageDescription">Damage Description</Column>
                      <Column id="housesDestroyed" width={100} helpModalId="volEventHousesDestroyed">Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrder" width={125} helpModalId="volEventHousesDestroyedDescription">Houses Destroyed Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Total Effects (Earthquake and Tsunami, Volcano, etc.)</CardTitle>
                    <SingleResultGrid data={volcanoEvent}>
                      <Column id="deathsTotal" width={100} helpModalId="volEventTotalDeaths">Total Deaths</Column>
                      <Column id="deathsAmountOrderTotal" width={125} helpModalId="volEventTotalDeathsDescription">Total Death Description</Column>
                      <Column id="missingTotal" width={100} helpModalId="volEventTotalMissing">Total Missing</Column>
                      <Column id="missingAmountOrderTotal" width={125} helpModalId="volEventTotalMissingDescription">Total Missing Description</Column>
                      <Column id="injuriesTotal" width={100} helpModalId="volEventTotalInjuries">Total Injuries</Column>
                      <Column id="injuriesAmountOrderTotal" width={125} helpModalId="volEventTotalInjuriesDescription">Total Injuries Description</Column>
                      <Column id="damageMillionsDollarsTotal" width={100} helpModalId="volEventTotalDamage">Total Damage ($Mil)</Column>
                      <Column id="damageAmountOrderTotal" width={125} helpModalId="volEventTotalDamageDescription">Total Damage Description</Column>
                      <Column id="housesDestroyedTotal" width={100} helpModalId="volEventTotalHousesDestroyed">Total Houses Destroyed</Column>
                      <Column id="housesDestroyedAmountOrderTotal" width={125} helpModalId="volEventTotalHousesDestroyedDescription">Total Houses Destroyed Description</Column>
                    </SingleResultGrid>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">Comments</CardTitle>
                    <CommentsSection title="">{volcanoEvent.get('comments')}</CommentsSection>
                  </CardBody>
                </Card>
              </div>

              <div className="haz-info-content-section haz-reference-info">
                <Card>
                  <CardBody>
                    <CardTitle tag="h2">References for the Volcano Event</CardTitle>
                    <ReferencesTable
                      references={references}
                      handleToggleColumns={this.handleToggleColumns}
                      sort={sort}
                      visibleColumns={visibleColumns}
                    />
                  </CardBody>
                </Card>
              </div>

            </div>

          </CardBody>
        </Card>

      </div>
    );
  }
}


VolcanoEventInfoPageUnwrapped.propTypes = {
  match: PropTypes.shape({ params: PropTypes.shape({ volcanoId: PropTypes.string }) }).isRequired,
  fetched: PropTypes.bool.isRequired,
  getVolcanoEventReferences: PropTypes.func.isRequired,
  getVolcanoEventInfo: PropTypes.func.isRequired,
  clearVolcanoEventInfo: PropTypes.func.isRequired,
  volcanoEvent: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.list.isRequired,
  sortReferences: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  toggleColumns: PropTypes.func.isRequired,
  authorities: ImmutablePropTypes.list.isRequired,
};

const mapStateToProps = (state) => {
  const fetchedVolcanoEvent = state.getIn(['volcanoEventInfoPage', 'fetchedVolcanoEvent']);
  const fetchedReferences = state.getIn(['volcanoEventInfoPage', 'fetchedReferences']);
  return {
    fetched: [fetchedVolcanoEvent, fetchedReferences].every(v => v),
    volcanoEvent: state.getIn(['volcanoEventInfoPage', 'volcanoEvent']),
    references: state.getIn(['volcanoEventInfoPage', 'references']),
    sort: state.getIn(['volcanoEventInfoPage', 'sort']),
    visibleColumns: state.getIn(['volcanoEventInfoPage', 'visibleColumns']),
    sortReferences: PropTypes.func.isRequired,
    toggleColumns: PropTypes.func.isRequired,
  };
};

const mapDispatchToProps = dispatch => bindActionCreators({
  getVolcanoEventReferences: getVolcanoEventReferencesAc,
  clearVolcanoEventInfo: clearVolcanoEventInfoAc,
  getVolcanoEventInfo: getVolcanoEventInfoAc,
  sortReferences: sortReferencesAc,
  toggleColumns: toggleColumnsAc,
}, dispatch);

export const VolcanoEventInfoPage = connect(mapStateToProps, mapDispatchToProps)(VolcanoEventInfoPageUnwrapped);
